import { Link } from 'react-router-dom';
import { line, fill, formatDateTime, t, zoneAbbreviation, type RequestStatus, type StringKey } from '@bms2/shared';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import styles from './DealRequestStatus.module.css';

/** Where the job stands as the Deal page's answer carries it (decision 1913). */
export interface RequestStatusView {
  status: RequestStatus;
  job: { id: string; number: string } | null;
  nextVisit: { windowStart: string; technicianName: string | null } | null;
}

const WORDS: Readonly<Record<RequestStatus, StringKey>> = {
  Cancelled: 'deal.request.status.cancelled',
  Closed: 'deal.request.status.closed',
  Completed: 'deal.request.status.completed',
  CouldNotComplete: 'deal.request.status.couldNotComplete',
  WorkInProgress: 'deal.request.status.workInProgress',
  QuoteDeclined: 'deal.request.status.quoteDeclined',
  QuoteSent: 'deal.request.status.quoteSent',
  New: 'deal.request.status.new',
};

/**
 * 🔒 *Where the job stands* (decision 1913): one line under the request's banner,
 * the derived word, the job as a link, and its next visit with the technician
 * leading it where one is booked, in the viewer's own zone. 🔨 Nothing stores
 * the word; the answer derives it on every read.
 */
export function DealRequestStatus({ view }: { view: RequestStatusView }) {
  const zone = useViewerZone();
  const format = useDateFormat();
  const visit = view.nextVisit;
  const when = visit ? line(formatDateTime(visit.windowStart, zone, format), zoneAbbreviation(visit.windowStart, zone)) : null;
  const visitWords = visit && when ? line('·', fill(t('deal.request.status.nextVisit'), { when }), visit.technicianName ? fill(t('deal.request.status.with'), { name: visit.technicianName ?? '' }) : null) : null;
  const heading = `${t('deal.request.status.heading')}:`;
  return (
    <p className={styles.line} data-deal-request-status={view.status}>
      <span className={styles.label}>{heading}</span>
      <span className={styles.status}>{t(WORDS[view.status] ?? 'deal.request.status.new')}</span>
      {view.job ? <span>{line('·')} <Link to={`/work-orders/${view.job.id}`}>{view.job.number}</Link></span> : null}
      {visitWords ? <span data-deal-request-next-visit="true">{visitWords}</span> : null}
    </p>
  );
}
